import type { ImportedCity, ResolvedBuilding } from '$lib/types'
import { normalize } from './normalize'
import { lookupReference } from './reference-catalogue'
import { getCached, setCached } from './cache'

/**
 * Given a `cityentity_id` and (optionally) the imported city it belongs to,
 * return a normalised `ResolvedBuilding`. Source priority:
 *
 *   1. The import's own `CityEntities` (always matches the player's export)
 *   2. The repo's reference catalogue (fallback for cross-import lookups)
 *
 * Returns `null` if the entity is unknown to both sources. Renderer + stats
 * decide how to surface unknowns; this resolver does not throw.
 *
 * Resolution is memoised per `(importedCityId, cityentityId)` because the
 * renderer may call this on every frame.
 */
export function resolveBuilding(
  cityentityId: string,
  importedCity?: ImportedCity,
): ResolvedBuilding | null {
  const cached = getCached(cityentityId, importedCity?.id)
  if (cached.hit) return cached.value

  const fromImport = importedCity?.parsed?.cityEntities?.[cityentityId]
  if (fromImport) {
    const resolved = normalize(cityentityId, fromImport)
    setCached(cityentityId, importedCity?.id, resolved)
    return resolved
  }

  const fromReference = lookupReference(cityentityId)
  setCached(cityentityId, importedCity?.id, fromReference)
  return fromReference
}
